import React, { useState, useEffect } from 'react';
import MainStructure from '../components/general/MainStructure';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import DatePicker from "react-datepicker";
import { toast } from 'react-toastify';
import Spinner from 'react-bootstrap/Spinner';
import Modal from 'react-bootstrap/Modal'; 
import Button from 'react-bootstrap/Button'; 
import VideoUploader from './VideoUploader';
import TimezoneSelector from './components/TimezoneSelector';

// 1. New Imports added here
import MediaPicker from './MediaPicker';
import * as Icon from 'react-feather';
import SlideshowEditor, { renderSlideToFile } from '../components/tiktok/SlideshowEditor';

const TiktokPost = () => {
  const myContext = useAuth();
  const navigate = useNavigate();
  
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [creatorInfo, setCreatorInfo] = useState(null);
  const [isLoadingInfo, setIsLoadingInfo] = useState(false);
  
  const [text, setText] = useState('');
  const [shortText, setShortText] = useState(''); 
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2. New State for Media Picker
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const [privacy, setPrivacy] = useState('');
  const [allowComment, setAllowComment] = useState(false); 
  const [allowStitch, setAllowStitch] = useState(false);   
  const [allowDuet, setAllowDuet] = useState(false);       

  const [contentDisclosure, setContentDisclosure] = useState(false); 
  const [isYourBrand, setIsYourBrand] = useState(false);
  const [isBrandedContent, setIsBrandedContent] = useState(false);
  const [agreedToCompliance, setAgreedToCompliance] = useState(false);
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Photo slideshow mode. TikTok calls these photo posts: 1–35 images, a
  // title of up to 90 characters and a description of up to 4,000.
  const [postType, setPostType] = useState('video');      // 'video' | 'photo'
  const [slides, setSlides] = useState([]);
  const [photoTitle, setPhotoTitle] = useState('');
  const [photoDescription, setPhotoDescription] = useState('');
  // 'auto' (the default): TikTok adds a recommended sound and publishes.
  // 'none': published without music. 'draft': sent to the creator's TikTok
  // drafts to pick a sound and publish in the app.
  const [musicMode, setMusicMode] = useState('auto');
  const [publishStep, setPublishStep] = useState('');
  const isPhoto = postType === 'photo';
  const isDraft = isPhoto && musicMode === 'draft';

  const tiktokAccounts = myContext.accounts.filter(a => a.type === 'tiktok');

  useEffect(() => {
    const fetchUserTimezone = async () => {
      try {
        const response = await fetch('https://api.schedpilot.com/wp-json/schedpilot/v1/get-settings', {
          method: 'GET',
          credentials: 'include',
        });
        const data = await response.json();
        if (response.ok && data.timezone) {
          setTimezone(data.timezone);
        }
      } catch (err) {
        console.error("Could not load preferred timezone", err);
      }
    };
    fetchUserTimezone();
  }, []);

  useEffect(() => {
    if (isBrandedContent && privacy === 'SELF_ONLY') {
      setPrivacy('PUBLIC_TO_EVERYONE'); 
      toast.info("Visibility switched to Public. Branded content cannot be private.");
    }
  }, [isBrandedContent]);

  const getComplianceText = () => {
    const musicLink = (
      <a 
        href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-blue-500 hover:underline"
        onClick={(e) => e.stopPropagation()} 
      >
        Music Usage Confirmation
      </a>
    );

    const brandedLink = (
      <a 
        href="https://www.tiktok.com/legal/page/global/bc-policy/en" 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-blue-500 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        <strong>Branded Content Policy</strong>
      </a>
    );

    return (
      <div className="form-check d-flex align-items-start">
        <input 
          className="form-check-input me-2 mt-1" 
          type="checkbox" 
          id="complianceCheckbox" 
          checked={agreedToCompliance}
          onChange={(e) => setAgreedToCompliance(e.target.checked)}
        />
        <label className="form-check-label cursor-pointer" style={{paddingTop:3}} htmlFor="complianceCheckbox">
          {isBrandedContent ? (
            <>By posting, you agree to TikTok's {brandedLink} and {musicLink}.</>
          ) : (
            <>By posting, you agree to TikTok's {musicLink}.</>
          )}
        </label>
      </div>
    );
  };

  const handleSelectAccount = async (account) => {
    setSelectedAccount(account);
    setAgreedToCompliance(false); 
    setIsLoadingInfo(true); 
    setCreatorInfo(null);
    setPrivacy('');
    setAllowComment(false);
    setAllowStitch(false);
    setAllowDuet(false);
    setContentDisclosure(false);
    setIsYourBrand(false);
    setIsBrandedContent(false);

    try {
      const response = await fetch(`https://api.schedpilot.com/wp-json/api/v1/tiktok-creator-info?account_id=${account.id}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setCreatorInfo(data.creator_info);
      } else {
        toast.error(data.message || "Could not retrieve TikTok info.");
      }
    } catch (err) {
      toast.error("Network error fetching TikTok details.");
    } finally {
      setIsLoadingInfo(false);
    }
  };

  const isCommercialInvalid = contentDisclosure && !isYourBrand && !isBrandedContent;

  const handlePostClick = () => {
    if (isPhoto) {
      if (!slides.length) return toast.error("Please add at least one photo.");
      if (photoTitle.length > 90) return toast.error("The title can be at most 90 characters.");
      if (photoDescription.length > 4000) return toast.error("The description can be at most 4,000 characters.");
    } else if (!uploadedVideos.length) return toast.error("Please upload a video.");
    if (!isDraft && !privacy) return toast.error("Please select a Privacy Status.");
    if (!isDraft && isCommercialInvalid) return toast.error("Please indicate if content promotes yourself or a third party.");
    
    setShowConfirmModal(true);
  };

  const formatLocalDate = (date) => {
    const pad = (num) => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  // Each slide is rendered to a 1080×1920 JPEG with its text burned in, then
  // uploaded like any image. Uploads run a few at a time but the ids keep the
  // slide order.
  const publishSlideshow = async () => {
    const ids = new Array(slides.length);
    let done = 0;
    const queue = slides.map((slide, i) => ({ slide, i }));
    const worker = async () => {
      while (queue.length) {
        const { slide, i } = queue.shift();
        const file = await renderSlideToFile(slide, i);
        const form = new FormData();
        form.append('image', file);
        const res = await fetch('https://api.schedpilot.com/wp-json/custom/v1/upload-image', { method: 'POST', credentials: 'include', body: form });
        const data = await res.json().catch(() => ({}));
        if (data.code !== 200 || !data.image_id) throw new Error(data.message || `Photo ${i + 1} could not be uploaded.`);
        ids[i] = data.image_id;
        done += 1;
        setPublishStep(`Preparing photos… ${done} of ${slides.length}`);
      }
    };
    setPublishStep(`Preparing photos… 0 of ${slides.length}`);
    await Promise.all([worker(), worker(), worker()]);

    setPublishStep('Scheduling…');
    const response = await fetch('https://api.schedpilot.com/wp-json/api/v1/create-tiktok-slideshow', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: photoTitle,
        description: photoDescription,
        slides: ids.map((image_id) => ({ image_id })),
        accounts: [`${selectedAccount.id}_tiktok`],
        scheduledDate: formatLocalDate(selectedDate),
        timezone,
        postStatus: 'publish',
        tiktok_metadata: {
          privacy_level: privacy,
          allow_comments: allowComment,
          content_disclosure: contentDisclosure,
          brand_organic: isYourBrand,
          branded_content: isBrandedContent,
          music_mode: musicMode,
          auto_add_music: musicMode === 'auto',
        },
      }),
    });
    const res = await response.json();
    if (res.code !== 200) throw new Error(res.message || 'Failed to schedule the slideshow.');
    return res;
  };

  const executeFinalPublish = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);

    if (isPhoto) {
      try {
        const res = await publishSlideshow();
        toast.success(isDraft
          ? "Slideshow scheduled! At that time it will land in your TikTok drafts — open the TikTok app to add a sound and publish."
          : "Slideshow scheduled! It may take a few minutes for TikTok to show it on your profile.");
        if (myContext.addScheduledItem) myContext.addScheduledItem(res.data);
        setTimeout(() => navigate('/dashboard'), 2000);
      } catch (err) {
        toast.error(err.message || "Failed to schedule the slideshow.");
      } finally {
        setIsSubmitting(false);
        setPublishStep('');
      }
      return;
    }

    try {
      const response = await fetch('https://api.schedpilot.com/wp-json/api/v1/schedulePost', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          scheduledDate: formatLocalDate(selectedDate),
          timezone: timezone,
          text: text, 
          videoTitle: shortText,
          postStatus: "publish",
          videos: uploadedVideos,
          images: [],
          accounts: [`${selectedAccount.id}_tiktok`],
          tiktok_metadata: {
            privacy_level: privacy,
            allow_comments: allowComment,
            allow_stitch: allowStitch,
            allow_duet: allowDuet,
            content_disclosure: contentDisclosure,
            brand_organic: isYourBrand,
            branded_content: isBrandedContent
          }
        }),
      });

      const MYJSON = await response.json();

      if (MYJSON.ok || response.ok) {
        toast.success("Post successfully scheduled! Note: It may take a few minutes for TikTok to process and display the video on your profile.");
        if(myContext.addScheduledItem) {
            myContext.addScheduledItem(MYJSON.data);
        }
        setTimeout(() => {
            navigate('/dashboard'); 
        }, 2000);
      } else {
        toast.error(MYJSON.message || "Failed to schedule TikTok post.");
      }
    } catch (error) {
      console.error('TikTok Post Error:', error);
      toast.error("A network error occurred while scheduling.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainStructure activeItem={"write-post"}>
      <div className="container p-0">
        <div className="row">
          <div className="col-12 col-md-7">
            <div className='d-flex mb-4 align-items-center'>
              <button onClick={() => navigate(-1)} className='btn btn-sm btn-outline-secondary me-3'>← Back</button>
              <h1 className="h3 mb-0 fw-bold">Post to TikTok</h1>
            </div>

            {!selectedAccount ? (
              <div className="card p-4 shadow-sm border-0">
                <h5 className="fw-bold mb-3">Choose a TikTok Account</h5>
                {
                tiktokAccounts.length > 0 ? (
                tiktokAccounts.map(acc => (
                  <div key={acc.id} className="d-flex align-items-center p-3 border rounded mb-3" onClick={() => handleSelectAccount(acc)} style={{cursor: 'pointer'}}>
                    <img src={acc.image} alt="" style={{width: 50, height: 50, borderRadius: '50%'}} className="me-3" />
                    <span className="fw-bold">{acc.name}</span>
                  </div>
                ))):
                (
                  <div className="text-center py-4">
                    <div className="mb-3">
                      <i className="bi bi-person-plus" style={{ fontSize: '2rem', color: '#6c757d' }}></i>
                    </div>
                    <p className="text-muted">No TikTok accounts connected yet.</p>
                    <button 
                      className="btn btn-primary"
                      onClick={() => navigate('/social-accounts')} 
                    >
                      Connect TikTok Account
                    </button>
                  </div>
                )
              }
              </div>
            ) : (
              <div className="fade-in">
                <div className="d-flex gap-2 mb-4" role="tablist" aria-label="What to post">
                  {[['video', 'Video', Icon.Film], ['photo', 'Photo slideshow', Icon.Image]].map(([key, label, Glyph]) => (
                    <button key={key} type="button" role="tab" aria-selected={postType === key}
                      onClick={() => setPostType(key)}
                      className={`btn flex-fill fw-bold ${postType === key ? 'btn-dark' : 'btn-outline-secondary'}`}>
                      <Glyph size={15} className="me-2" />{label}
                    </button>
                  ))}
                </div>

                {!isPhoto && uploadedVideos.length > 0 && (
                  <div className="mb-4 card p-2 bg-dark shadow-sm">
                    <video 
                      src={typeof uploadedVideos[0] === 'string' ? uploadedVideos[0] : (uploadedVideos[0].file_url || uploadedVideos[0].url)} 
                      controls 
                      style={{ maxHeight: '350px', width: '100%', borderRadius: '4px' }} 
                    />
                    <div className="text-white small p-2 text-center">Video Preview</div>
                  </div>
                )}

                <div className="card p-3 shadow-sm mb-4 border-primary bg-light">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <img src={creatorInfo?.avatar_url || selectedAccount.image} style={{borderRadius: '50%', height: 50, width: 50}} className="me-3" />
                      <div>
                        <h6 className="mb-0">Posting as <strong>@{creatorInfo?.nickname || selectedAccount.name}</strong></h6>
                      </div>
                    </div>
                    <button className="btn btn-sm btn-link text-muted" onClick={() => setSelectedAccount(null)}>Switch Account</button>
                  </div>
                </div>

                {isPhoto ? (
                  <>
                    <div className="mb-3">
                      <label className="fw-bold mb-1">Title</label>
                      <input className="form-control" value={photoTitle} maxLength={110} onChange={(e) => setPhotoTitle(e.target.value)} placeholder="A short title for the slideshow" />
                      <div className="small text-end" style={{ color: photoTitle.length > 90 ? '#dc2626' : '#94a3b8' }}>{photoTitle.length}/90</div>
                    </div>
                    <div className="mb-3">
                      <label className="fw-bold mb-1">Description</label>
                      <textarea className="form-control" rows="3" value={photoDescription} onChange={(e) => setPhotoDescription(e.target.value)} placeholder="Describe your slideshow, add #hashtags…" />
                      <div className="small text-end" style={{ color: photoDescription.length > 4000 ? '#dc2626' : '#94a3b8' }}>{photoDescription.length}/4000</div>
                    </div>
                  </>
                ) : (
                <div className="mb-3">
                  <label className="fw-bold mb-1">Video Caption</label>
                  <textarea 
                    className="form-control" 
                    placeholder="Add a title that describes your video..." 
                    value={shortText} 
                    onChange={(e) => setShortText(e.target.value)} 
                    rows="3" 
                  />
                </div>
                )}

                {isPhoto && (
                  <div className="mb-4 card p-3 border-0 shadow-sm">
                    <label className="fw-bold mb-2">Music</label>
                    {[
                      ['auto', 'Let TikTok choose the music', 'TikTok adds a recommended sound and publishes the slideshow at the scheduled time.'],
                      ['none', 'No music', 'Published at the scheduled time without a sound.'],
                      ['draft', 'Send to my TikTok drafts to pick the sound', "At the scheduled time it lands in your TikTok inbox. Open the TikTok app to choose any sound, then publish it yourself."],
                    ].map(([value, label, hint]) => (
                      <div className="form-check mb-2" key={value}>
                        <input className="form-check-input" type="radio" name="musicMode" id={`music-${value}`} value={value}
                          checked={musicMode === value} onChange={() => setMusicMode(value)} />
                        <label className="form-check-label" htmlFor={`music-${value}`}>
                          <span className="fw-semibold">{label}</span>
                          <div className="small text-muted">{hint}</div>
                        </label>
                      </div>
                    ))}
                  </div>
                )}

                {isDraft ? (
                  <div className="alert alert-light border small mb-4">
                    Visibility, comments and branded-content disclosure are set in the TikTok app when you publish the draft.
                  </div>
                ) : (
                <>
                <div className="mb-4">
                  <label className="fw-bold mb-1">Who can view this {isPhoto ? 'post' : 'video'}</label>
                  <select className="form-select" value={privacy} onChange={(e) => setPrivacy(e.target.value)}>
                    <option value="">Select who can view (Required)</option>
                    {creatorInfo?.privacy_level_options?.map((opt) => {
                      const isPrivateDisabled = opt === 'SELF_ONLY' && isBrandedContent;
                      return (
                        <option key={opt} value={opt} disabled={isPrivateDisabled}>
                          {opt.replace(/_/g, ' ')} {isPrivateDisabled ? "(Private not allowed for Branded Content)" : ""}
                        </option>
                      )
                    })}
                  </select>
                </div>

                <div className="mb-4 card p-3 border-0 shadow-sm">
                  <label className="fw-bold mb-2">Allow Interactions</label>
                  <div className="d-flex gap-3">
                    {(isPhoto ? ['comment'] : ['comment', 'stitch', 'duet']).map((type) => {
                       const isDisabled = creatorInfo?.[`${type}_disabled`];
                       const state = type === 'comment' ? allowComment : type === 'stitch' ? allowStitch : allowDuet;
                       const setter = type === 'comment' ? setAllowComment : type === 'stitch' ? setAllowStitch : setAllowDuet;
                       return (
                        <div className="form-check" key={type}>
                          <input type="checkbox" className="form-check-input" checked={state} disabled={isDisabled} onChange={(e) => setter(e.target.checked)} id={type} />
                          <label className="form-check-label text-capitalize" htmlFor={type}>{type}</label>
                        </div>
                       )
                    })}
                  </div>
                </div>

                <div className="mb-4 card p-3 border-info shadow-sm" style={{backgroundColor: '#f8feff'}}>
                  <div className="form-check form-switch mb-2">
                    <input className="form-check-input" type="checkbox" id="disclosureToggle" checked={contentDisclosure} onChange={(e) => {
                        setContentDisclosure(e.target.checked);
                        if(!e.target.checked) { setIsYourBrand(false); setIsBrandedContent(false); }
                      }} 
                    />
                    <label className="form-check-label fw-bold" htmlFor="disclosureToggle">Disclose {isPhoto ? 'Post' : 'Video'} Content</label>
                  </div>

                  <p>Turn on to disclose that this video promotes goods or services in exchange for something of value. Your video will 
                    be labeled as "promotional content". This cannot be changed once your video is posted.
                  </p>

                  {contentDisclosure && (
                    <div className="mt-2 ps-3 border-start border-info border-3">
                      <div className="form-check mb-2">
                        <input className="form-check-input" type="checkbox" checked={isYourBrand} onChange={(e) => setIsYourBrand(e.target.checked)} id="brandMe" />
                        <label className="form-check-label" htmlFor="brandMe">Your brand</label>
                         <div className="text-info">You are promoting yourself or your own business. This video will be classified as Brand Organic.</div> 
                      </div>
                      <div className="form-check mb-2">
                        <input className="form-check-input" type="checkbox" checked={isBrandedContent} onChange={(e) => setIsBrandedContent(e.target.checked)} id="brandOther" />
                        <label className="form-check-label" htmlFor="brandOther">Branded content</label>
                        <div className="text-info  ">You are promoting another brand or a third party. This video will be classified as Branded Content.</div>
                      </div>
                    </div>
                  )}
                </div>
                </>
                )}

                {isPhoto ? (
                  <>
                    <h5 className="fw-bold mt-4">Photos</h5>
                    <SlideshowEditor slides={slides} setSlides={setSlides} />
                  </>
                ) : (
                <>
                <h5 className="fw-bold mt-4">Upload Video</h5>
                <VideoUploader uploadedVideos={uploadedVideos} setUploadedVideos={setUploadedVideos} maxDuration={creatorInfo?.max_video_post_duration_sec} />
                
                {/* 3. Added Media Library Trigger Button here */}
                <div className="mt-2">
                    <Button variant="outline-secondary" size="sm" onClick={() => setShowMediaPicker(true)}>
                        <Icon.Database size={14} className="me-1" /> Choose from Media Library
                    </Button>
                </div>

                {/* 4. Added MediaPicker Component here */}
                <MediaPicker 
                    show={showMediaPicker} 
                    onHide={() => setShowMediaPicker(false)} 
                    type="videos"
                    onSelect={(video) => {
                        if (!uploadedVideos.find(v => v.id === video.id)) {
                            setUploadedVideos([...uploadedVideos, video]);
                        }
                        setShowMediaPicker(false);
                        toast.success("Video added from library!");
                    }}
                />
                </>
                )}
              </div>
            )}
          </div>

          <div className="col-12 col-md-5">
            <div className="card p-4 shadow-sm border-0 sticky-top" style={{top: '20px'}}>
              <h5 className="fw-bold mb-3">Scheduling</h5>
              <DatePicker selected={selectedDate} onChange={date => setSelectedDate(date)} showTimeSelect dateFormat="MMMM d, yyyy h:mm aa" className="form-control mb-4" />
              
              <TimezoneSelector 
                value={timezone} 
                onChange={setTimezone} 
                label="Scheduling Timezone"
                className="mb-5"
              />

              <div className="alert alert-light border small mb-3">
                {getComplianceText()}
              </div>

              <div title={isCommercialInvalid ? "You need to indicate if your content promotes yourself, a third party, or both." : ""}>
                <button 
                  className="btn btn-dark w-100 py-3 fw-bold"
                  disabled={isSubmitting || !creatorInfo?.can_post || (isPhoto ? !slides.length : !uploadedVideos.length) || (!isDraft && !privacy) || (!isDraft && isCommercialInvalid) ||  !agreedToCompliance}
                  onClick={handlePostClick}
                >
                  {isSubmitting && publishStep ? publishStep : isPhoto ? `Post Slideshow${slides.length ? ` (${slides.length} photo${slides.length === 1 ? '' : 's'})` : ''}` : 'Upload Video'}
                </button>
              </div>

              <p className="text-muted small mt-3 text-center">
                After publishing, it may take a few minutes for the content to process and be visible on your profile.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Express Consent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You are about to publish this {isPhoto ? `slideshow of ${slides.length} photo${slides.length === 1 ? '' : 's'}` : 'video'} to <strong>@{creatorInfo?.nickname}</strong>.</p>
          <div className="bg-light p-3 rounded mb-3">
            <div className="small text-muted">Title: {(isPhoto ? photoTitle : shortText) || 'No title'}</div>
            {isDraft
              ? <div className="small text-muted">Sent to your TikTok drafts — you'll add the sound and publish in the TikTok app.</div>
              : <div className="small text-muted">Visibility: {privacy.replace(/_/g, ' ')}</div>}
            {isPhoto && !isDraft && (
              <div className="small text-muted">Music: {musicMode === 'auto' ? 'chosen by TikTok' : 'none'}</div>
            )}
            {contentDisclosure && (
              <div className="small text-info">Disclosure: {isBrandedContent ? 'Branded Content' : 'Your Brand'}</div>
            )}
          </div>
          <p className="mb-0">By clicking <strong>"Agree & Publish"</strong>, you expressly consent to uploading this content and its metadata to TikTok.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowConfirmModal(false)}>
            Go Back
          </Button>
          <Button variant="primary" onClick={executeFinalPublish} disabled={isSubmitting}>
            {isSubmitting ? 'Publishing...' : 'Agree & Publish'}
          </Button>
        </Modal.Footer>
      </Modal>

    </MainStructure>
  );
};

export default TiktokPost;